import * as React from "react";
import { debounce } from "lodash";
import { CircularProgress, TextField } from "@mui/material";
import CustomAutocomplete from "./AutoComplete";
import get from "lodash/get";

const makeDefaultOption = (config) => {
    if (!config) return {};
    const { value, label } = config;
    return { [value]: "", [label]: "" };
};

const CustomAutoComplete = ({ field, methods, execute }) => {
    const defaultOption = makeDefaultOption(field.optionConfig);

    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const loading = field.props?.loading ?? false;
    const {
        // control,
        getValues,
        setValue,
        // trigger,
        formState: { errors, defaultValues },
    } = methods;

    // Check if field has dependent params (functions that need fresh values)
    const hasDependentParams = React.useMemo(() => {
        const params = field?.async?.params;
        if (!params) return false;
        return Object.values(params).some(value => typeof value === 'function');
    }, [field?.async?.params]);

    // Handle open - only refetch if field has dependent params
    const handleOpen = React.useCallback(() => {
        setOpen(true);
        if (execute && hasDependentParams) {
            execute();
        }
    }, [execute, hasDependentParams]);

    React.useEffect(() => {
        const fieldOptions = Array.isArray(field.options) ? field.options : [];
        setOptions([defaultOption, ...fieldOptions]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.options]);

    React.useEffect(() => {
        if (
            getValues(field.attribute) === defaultValues?.[field.attribute] &&
            options.length === 1 &&
            execute
        ) {
            execute();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getValues(field.attribute)]);

    React.useEffect(() => {
        if (!open) {
            // setOptions([defaultOption]);
        }
    }, [open]);

    const fieldConfig = React.useMemo(() => {
        const filterAsync = !!field.filterAsync;
        const config = {
            ...field,
            options,
            props: {
                ...field?.props,
                autoComplete: true,
                onOpen: handleOpen,
                onClose: () => {
                    setOpen(false);
                },
                renderInput: (params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={fieldConfig.label}
                        error={!!get(errors, fieldConfig.attribute)}
                        helperText={get(errors, fieldConfig.attribute)?.message}
                        sx={{
                            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                                padding: ` ${
                                    fieldConfig.props?.multiple ? "6" : "8"
                                }px 0px !important`,
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            style: {},
                            // readOnly: fieldConfig.readOnly,
                            endAdornment: (
                                <>
                                    {loading ? (
                                        <CircularProgress
                                            color="inherit"
                                            size={20}
                                        />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                ),
            },
        };
        if (filterAsync) {
            config.props = {
                ...config.props,
                autoComplete: true,
                filterSelectedOptions: true,
                filterOptions: (x) => x,
                onInputChange: debounce((e, val, reason) => {
                    if (reason !== "input") return;
                    setOptions([defaultOption]);
                    if (!val) {
                        setValue(field?.attribute, "");
                    }
                    field.onChange(val);
                }, 1000),
            };
        }
        return config;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, open, errors[field?.attribute], handleOpen]);

    return <CustomAutocomplete field={fieldConfig} methods={methods} />;
};

export default CustomAutoComplete;
